import { useEffect, useState } from "react";
import { X, MessageCircle, Mail } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, formatPrice } from "../lib/api";

export default function CheckoutDialog({ onClose }) {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [contact, setContact] = useState({ email: "", whatsapp: "" });

  useEffect(() => {
    api.get("/contact").then((r) => setContact(r.data)).catch(() => {});
  }, []);

  const orderLines = items
    .map((i) => `- ${i.qty} x ${i.name} (${formatPrice(i.price)}) = ${formatPrice(i.price * i.qty)}`)
    .join("\n");
  const body =
    `Bonjour Bidoudou50,\n\nJe souhaite passer la commande suivante :\n\n${orderLines}\n\n` +
    `Total : ${formatPrice(total)}\n\n` +
    `Nom : ${form.name}\nEmail : ${form.email}\nTéléphone : ${form.phone}\n\n` +
    `Note : ${form.note || "—"}\n\nMerci !`;

  const mailto = `mailto:${contact.email}?subject=${encodeURIComponent(
    "Commande Bidoudou50"
  )}&body=${encodeURIComponent(body)}`;

  const waLink = `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(body)}`;

  const valid = form.name.trim() && form.email.trim();

  return (
    <div
      data-testid="checkout-dialog"
      className="fixed inset-0 z-[60] bg-stone-900/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-stone-50 max-w-lg w-full rounded-sm border border-stone-200 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-stone-200">
          <h2 className="font-serif text-2xl text-stone-900">Finaliser ma commande</h2>
          <button
            onClick={onClose}
            data-testid="close-checkout-button"
            className="p-2 hover:bg-stone-200 rounded-sm"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-stone-600">
            Nous ne traitons pas le paiement en ligne. Remplissez vos coordonnées puis envoyez votre commande par <strong>email</strong> ou <strong>WhatsApp</strong>. Nous reviendrons vers vous très rapidement.
          </p>

          <div className="space-y-3">
            <input
              data-testid="checkout-name"
              placeholder="Nom complet *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
            />
            <input
              data-testid="checkout-email"
              type="email"
              placeholder="Email *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
            />
            <input
              data-testid="checkout-phone"
              placeholder="Téléphone (optionnel)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
            />
            <textarea
              data-testid="checkout-note"
              placeholder="Précisions sur la gravure souhaitée (texte, police, etc.)"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
            />
          </div>

          <div className="bg-white border border-stone-200 p-4 rounded-sm">
            <div className="flex items-center justify-between mb-2 text-sm text-stone-500 uppercase tracking-[0.15em]">
              <span>Récapitulatif</span>
              <span>{items.length} article{items.length > 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone-700">Total</span>
              <span data-testid="checkout-total" className="text-2xl font-black text-orange-700">{formatPrice(total)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <a
              data-testid="send-whatsapp-button"
              href={valid ? waLink : undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!valid}
              onClick={(e) => {
                if (!valid) {
                  e.preventDefault();
                  return;
                }
                setTimeout(() => { clear(); onClose(); }, 300);
              }}
              className={`inline-flex items-center justify-center gap-2 py-3 rounded-sm font-medium transition-colors ${
                valid
                  ? "bg-[#25D366] hover:bg-[#1ebd57] text-white"
                  : "bg-stone-200 text-stone-400 cursor-not-allowed"
              }`}
            >
              <MessageCircle size={18} />
              Envoyer via WhatsApp
            </a>
            <a
              data-testid="send-email-button"
              href={valid ? mailto : undefined}
              aria-disabled={!valid}
              onClick={(e) => {
                if (!valid) {
                  e.preventDefault();
                  return;
                }
                setTimeout(() => { clear(); onClose(); }, 300);
              }}
              className={`inline-flex items-center justify-center gap-2 py-3 rounded-sm font-medium transition-colors ${
                valid
                  ? "bg-stone-900 hover:bg-stone-800 text-white"
                  : "bg-stone-200 text-stone-400 cursor-not-allowed"
              }`}
            >
              <Mail size={18} />
              Envoyer par Email
            </a>
          </div>

          {!valid && (
            <p className="text-xs text-stone-500 text-center">Renseignez votre nom et email pour activer l'envoi.</p>
          )}
        </div>
      </div>
    </div>
  );
}
